import { useEffect, useState, type ReactNode } from 'react';
import { CLINIC_URL } from '@/config';
import {
  ArrowRight,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  Globe,
  Menu,
  PlayCircle,
  Receipt,
  ShieldCheck,
  Smartphone,
  HeartHandshake,
  X,
  Stethoscope,
  Clock,
  Activity,
  Star,
  Moon,
  Sun
} from 'lucide-react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { useTranslation, useTheme } from '@doccareplus/shared/lib';
import { cn } from '@doccareplus/shared/lib/utils';

// --- Shared Components ---

function Container({ children, className, frame = true }: { children: ReactNode; className?: string; frame?: boolean }) {
  return (
    <div className={cn('relative mx-auto w-full max-w-[1800px] px-5 sm:px-8 lg:px-12 xl:px-16', className)}>
      {frame && (
        <>
          <span className="pointer-events-none absolute inset-y-0 left-0 hidden w-px bg-[var(--border)] lg:block" />
          <span className="pointer-events-none absolute inset-y-0 right-0 hidden w-px bg-[var(--border)] lg:block" />
        </>
      )}
      {children}
    </div>
  );
}

function Section({ id, children, className, muted }: { id: string; children: ReactNode; className?: string; muted?: boolean }) {
  return (
    <section id={id} className={cn('relative scroll-mt-24 py-10 sm:py-16 lg:py-20', muted && 'bg-[var(--landing-surface-2)]/40', className)}>
      <Container>{children}</Container>
    </section>
  );
}

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 40 },
  show: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
      delay
    }
  }),
};

function Reveal({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  return (
    <motion.div className={className} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2, margin: "0px 0px -100px 0px" }} variants={fadeUp} custom={delay}>
      {children}
    </motion.div>
  );
}

const fadeScale: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  show: (delay = 0) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
      delay
    }
  }),
};

function RevealScale({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  return (
    <motion.div className={className} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2, margin: "0px 0px -50px 0px" }} variants={fadeScale} custom={delay}>
      {children}
    </motion.div>
  );
}

function GlassCard({ children, className, hover = true }: { children: ReactNode; className?: string; hover?: boolean }) {
  return (
    <motion.div whileHover={hover ? { y: -3 } : undefined} transition={{ type: 'spring', stiffness: 400, damping: 30 }} className={cn('landing-card rounded-[20px] bg-[var(--surface)] border border-[var(--border)] shadow-sm', className)}>
      {children}
    </motion.div>
  );
}

function MotionButton({ href, children, variant = 'primary', onClick, className, target, rel }: { href?: string; children: ReactNode; variant?: 'primary' | 'secondary' | 'outline'; onClick?: () => void; className?: string; target?: string; rel?: string }) {
  const classes = cn(
    'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-14 font-semibold transition-all hover:no-underline shadow-sm',
    variant === 'primary' && 'bg-primary hover:bg-primary-hover',
    variant === 'secondary' && 'bg-surface-2 border border-border hover:bg-surface-3',
    variant === 'outline' && 'border border-border bg-transparent hover:bg-surface-2',
    className
  ) + ' ' + (
    variant === 'primary'
      ? 'text-primary-fg hover:text-primary-fg'
      : 'text-text hover:text-text'
  );
  const props = { whileTap: { scale: 0.98 }, className: classes, onClick };
  // When opening in a new tab, always harden against reverse-tabnabbing.
  const safeRel = target === '_blank' ? (rel ?? 'noopener noreferrer') : rel;
  return href ? <motion.a href={href} target={target} rel={safeRel} {...props}>{children}</motion.a> : <motion.button type="button" {...props}>{children}</motion.button>;
}

// --- Sections ---

const languageOptions = [
  { code: 'en', label: 'English', sub: 'English' },
  { code: 'hi', label: 'हिंदी', sub: 'Hindi' },
  { code: 'hin', label: 'Hinglish', sub: 'Hindi (Latin script)' }
] as const;

function LanguageBottomSheet({
  isOpen,
  onClose,
  currentLanguage,
  onSelectLanguage
}: {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
  onSelectLanguage: (code: 'en' | 'hi' | 'hin') => void;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm lg:hidden"
          />

          {/* Bottom Sheet modal */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-x-0 bottom-0 z-[101] rounded-t-[28px] border-t border-border bg-surface px-6 pb-8 pt-4 shadow-2xl lg:hidden max-h-[85vh] overflow-y-auto"
          >
            {/* grab handle */}
            <div className="mx-auto mb-5 h-1.5 w-12 rounded-full bg-border-strong/60" />

            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-20 font-bold text-text">Choose Language / भाषा चुनें</h2>
                <p className="text-13 text-text-3">Select your preferred display language</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-2 text-text-2 hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-3">
              {languageOptions.map((option) => {
                const active = option.code === currentLanguage;
                return (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => {
                      onSelectLanguage(option.code);
                      onClose();
                    }}
                    className={cn(
                      "flex items-center justify-between rounded-2xl border p-4 text-left transition-all duration-200",
                      active
                        ? "border-primary bg-primary-weak text-primary shadow-sm"
                        : "border-border bg-surface text-text hover:bg-surface-2"
                    )}
                  >
                    <div className="flex flex-col">
                      <span className="text-16 font-bold">{option.label}</span>
                      <span className={cn("text-12 font-medium mt-0.5", active ? "text-primary/85" : "text-text-3")}>
                        {option.sub}
                      </span>
                    </div>
                    {active && (
                      <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-fg">
                        <Check size={14} strokeWidth={3} />
                      </motion.div>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Navbar() {
  const { t, i18n } = useTranslation('dashboard');
  const { resolvedTheme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [langSheetOpen, setLangSheetOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={cn('fixed inset-x-0 top-0 z-50 transition-all duration-300 border-b', scrolled ? 'bg-[var(--surface)]/80 backdrop-blur-xl border-[var(--border)] shadow-sm py-3' : 'bg-transparent border-transparent py-5')}>
      <Container frame={false}>
        <div className="relative flex items-center justify-between">
          <a href="#top" className="flex items-center hover:no-underline">
            <img src="/long_logo.svg" alt="DocCare+" className="h-8 w-auto" />
          </a>

          <nav className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-6 lg:flex">
            <a href="#about" className="text-14 font-medium text-[var(--text-2)] hover:text-[var(--text)] transition-colors hover:no-underline">{t('landing.static.nav.about')}</a>
            <a href="#features" className="text-14 font-medium text-[var(--text-2)] hover:text-[var(--text)] transition-colors hover:no-underline">{t('landing.static.nav.features')}</a>
            <a href="#pricing" className="text-14 font-medium text-[var(--text-2)] hover:text-[var(--text)] transition-colors hover:no-underline">{t('landing.static.nav.pricing')}</a>
            <a href="#contact" className="text-14 font-medium text-[var(--text-2)] hover:text-[var(--text)] transition-colors hover:no-underline">{t('landing.static.nav.contact')}</a>
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <div className="relative">
              <button type="button" onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 rounded-full px-3 py-2 text-13 font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] transition-colors">
                <Globe size={14} /> {i18n.language === 'hi' ? 'HI' : i18n.language === 'hin' ? 'HIN' : 'EN'} <ChevronDown size={14} />
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div initial={{ opacity: 0, y: 8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.95 }} transition={{ duration: 0.15 }} className="absolute right-0 top-full mt-2 w-36 overflow-hidden rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-1 shadow-xl">
                    <button onClick={() => { i18n.changeLanguage('en'); setLangOpen(false); }} className={cn("w-full rounded-[10px] px-3 py-2 text-left text-13 font-medium transition-colors", i18n.language === 'en' ? "bg-[var(--primary-weak)] text-[var(--primary)]" : "text-[var(--text-2)] hover:bg-[var(--surface-2)]")}>English</button>
                    <button onClick={() => { i18n.changeLanguage('hi'); setLangOpen(false); }} className={cn("w-full rounded-[10px] px-3 py-2 text-left text-13 font-medium transition-colors", i18n.language === 'hi' ? "bg-[var(--primary-weak)] text-[var(--primary)]" : "text-[var(--text-2)] hover:bg-[var(--surface-2)]")}>हिंदी</button>
                    <button onClick={() => { i18n.changeLanguage('hin'); setLangOpen(false); }} className={cn("w-full rounded-[10px] px-3 py-2 text-left text-13 font-medium transition-colors", i18n.language === 'hin' ? "bg-[var(--primary-weak)] text-[var(--primary)]" : "text-[var(--text-2)] hover:bg-[var(--surface-2)]")}>Hinglish</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button type="button" onClick={toggleTheme} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-[var(--surface-2)] text-[var(--text-2)] hover:text-[var(--text)] transition-colors">
              {resolvedTheme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <a href={`${CLINIC_URL}/login`} className="px-2 text-14 font-medium text-[var(--text-2)] transition-colors hover:text-[var(--text)] hover:no-underline">{t('landing.static.nav.login')}</a>
            <MotionButton href={`${CLINIC_URL}/register`}>{t('landing.static.nav.startTrial')}</MotionButton>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <button
              type="button"
              onClick={() => setLangSheetOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface-2 text-text transition-colors hover:bg-surface-3"
            >
              <Globe size={18} />
            </button>
            <button type="button" onClick={() => setOpen(!open)} className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] text-[var(--text)]">
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </Container>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0, filter: 'blur(10px)' }}
            animate={{ opacity: 1, height: 'auto', filter: 'blur(0px)' }}
            exit={{ opacity: 0, height: 0, filter: 'blur(10px)' }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-0 right-0 top-full border-t border-[var(--border)] bg-[var(--surface)]/95 backdrop-blur-2xl lg:hidden overflow-hidden shadow-2xl"
          >
            <motion.div
              initial="hidden" animate="show" exit="hidden"
              variants={{
                hidden: { opacity: 0 },
                show: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.1 } }
              }}
              className="flex flex-col p-6 sm:p-8"
            >
              <div className="flex flex-col gap-1">
                {[
                  { href: '#about', label: t('landing.static.nav.about') },
                  { href: '#features', label: t('landing.static.nav.features') },
                  { href: '#pricing', label: t('landing.static.nav.pricing') },
                  { href: '#contact', label: t('landing.static.nav.contact') },
                ].map((item) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpen(false);
                      setTimeout(() => {
                        const targetId = item.href.substring(1);
                        const target = document.getElementById(targetId);
                        if (target) {
                          target.scrollIntoView({ behavior: 'smooth' });
                        }
                      }, 300);
                    }}
                    variants={{ hidden: { opacity: 0, x: -10 }, show: { opacity: 1, x: 0 } }}
                    className="flex items-center justify-between rounded-xl p-3 sm:p-4 text-18 font-semibold text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] transition-colors hover:no-underline"
                  >
                    {item.label}
                    <ArrowRight size={18} className="opacity-30" />
                  </motion.a>
                ))}
              </div>

              <motion.hr variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }} className="my-6 border-[var(--border)]" />

              <motion.div variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }} className="flex flex-col gap-3">
                <MotionButton href={`${CLINIC_URL}/login`} variant="secondary" className="w-full justify-center text-16 py-3.5">{t('landing.static.nav.login')}</MotionButton>
                <MotionButton href={`${CLINIC_URL}/register`} variant="primary" className="w-full justify-center text-16 py-3.5">{t('landing.static.nav.startTrial')}</MotionButton>
              </motion.div>

              <motion.div
                variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
                className="flex items-center justify-between mt-6 rounded-xl bg-surface-2 p-3"
              >
                <span className="text-14 font-medium text-text-2">Theme</span>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="flex h-9 items-center gap-2 rounded-lg bg-surface px-4 text-13 font-semibold text-text shadow-sm transition-colors hover:text-primary"
                >
                  {resolvedTheme === 'dark' ? (
                    <>
                      <Sun size={14} /> Light mode
                    </>
                  ) : (
                    <>
                      <Moon size={14} /> Dark mode
                    </>
                  )}
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <LanguageBottomSheet
        isOpen={langSheetOpen}
        onClose={() => setLangSheetOpen(false)}
        currentLanguage={i18n.language}
        onSelectLanguage={(code) => i18n.changeLanguage(code)}
      />
    </header>
  );
}

function Hero() {
  const { t } = useTranslation('dashboard');
  return (
    <section id="top" className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24">
      <div className="landing-grain-mesh pointer-events-none absolute inset-0 -z-10" />

      <Container className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="flex flex-col items-start gap-6 text-left">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--surface)] px-4 py-1.5 text-12 font-semibold text-[var(--primary)] shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--primary)]" />
              {t('landing.reference.hero.badge')}
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="text-3xl font-semibold leading-tight text-[var(--text)] sm:text-4xl lg:text-5xl">
              {t('landing.reference.hero.titleA')} <br className="hidden sm:block" />
              <span className="text-[var(--primary)]">{t('landing.reference.hero.titleB')}</span> <br className="hidden sm:block" />
              {t('landing.reference.hero.titleC')}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="max-w-lg text-16 leading-relaxed text-[var(--text-2)] font-light">
              {t('landing.reference.hero.body')}
            </p>
          </Reveal>
          <Reveal delay={0.3} className="flex flex-col gap-3 sm:flex-row mt-2">
            <MotionButton href={`${CLINIC_URL}/register`} variant="primary">
              {t('landing.reference.hero.trial')} <ArrowRight size={16} />
            </MotionButton>
            <MotionButton
              href="https://docs.google.com/forms/d/e/1FAIpQLSdZItyO9tTPvgt_xrMyZqLLeldGQOcARPE2bqmVhs6zLLHOrw/viewform?usp=publish-editor"
              variant="outline"
              target="_blank"
            >
              <PlayCircle size={16} /> {t('landing.reference.hero.demo')}
            </MotionButton>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="text-13 text-[var(--text-3)] font-medium">
              {t('landing.reference.hero.note')}
            </p>
          </Reveal>
        </div>

        {/* Sleek Visual Abstract using app's actual CSS variables */}
        <Reveal delay={0.2} className="relative mx-auto w-full max-w-lg lg:max-w-none">
          <div className="relative overflow-hidden rounded-[24px] border border-[var(--border)] bg-[var(--surface-2)] shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--surface)] px-6 py-4">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--primary-bg)] text-18 font-bold text-[var(--primary)]">
                  RS
                </div>
                <div>
                  <h3 className="text-16 font-semibold text-[var(--text)]">Rahul Sharma</h3>
                  <p className="text-13 text-[var(--text-2)]">32 Yrs • Male • O+</p>
                </div>
              </div>
              <motion.a
                href={`${CLINIC_URL}/login`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-2 text-13 font-medium text-[var(--primary-fg)] shadow-sm transition-colors hover:bg-[var(--primary-hover)] hover:no-underline hover:text-[var(--primary-fg)]"
              >
                <CalendarClock size={16} />
                <span className="hidden sm:inline">{t('landing.static.heroVisual.bookVisit')}</span>
              </motion.a>
            </div>

            {/* Body */}
            <div className="grid sm:grid-cols-[1fr_1.5fr]">
              {/* Left Column: Vitals */}
              <div className="border-b border-[var(--border)] bg-[var(--surface-2)] p-6 sm:border-b-0 sm:border-r">
                <p className="mb-4 text-12 font-semibold uppercase tracking-widest text-[var(--text-3)]">{t('landing.static.heroVisual.latestVitals')}</p>
                <div className="flex flex-col gap-3">
                  {[
                    { label: t('landing.static.heroVisual.bp'), value: '120/80', unit: 'mmHg', color: 'text-[var(--danger)] bg-[var(--danger-bg)]', icon: Activity },
                    { label: t('landing.static.heroVisual.weight'), value: '72', unit: 'kg', color: 'text-[var(--info)] bg-[var(--info-bg)]', icon: Activity },
                    { label: t('landing.static.heroVisual.temp'), value: '98.6', unit: '°F', color: 'text-[var(--warning)] bg-[var(--warning-bg)]', icon: Activity },
                  ].map((vital, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn("flex h-8 w-8 items-center justify-center rounded-lg", vital.color)}>
                          <vital.icon size={14} />
                        </div>
                        <p className="text-13 font-medium text-[var(--text-2)]">{vital.label}</p>
                      </div>
                      <p className="text-14 font-semibold text-[var(--text)]">{vital.value} <span className="text-11 font-normal text-[var(--text-3)]">{vital.unit}</span></p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Right Column: Timeline */}
              <div className="bg-[var(--surface)] p-6">
                <p className="mb-4 text-12 font-semibold uppercase tracking-widest text-[var(--text-3)]">{t('landing.static.heroVisual.pastVisits')}</p>
                <div className="relative ml-3 space-y-6 border-l-2 border-[var(--border-strong)]">
                  {[
                    { date: 'Today, 10:30 AM', title: 'Viral Fever Follow-up', doctor: t('landing.static.heroVisual.docName'), status: t('landing.static.heroVisual.status1'), dot: 'bg-[var(--success)]' },
                    { date: '12 Aug 2026', title: 'Initial Consultation', doctor: t('landing.static.heroVisual.docName'), status: t('landing.static.heroVisual.status2'), dot: 'bg-[var(--border-strong)]' },
                  ].map((visit, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 + i * 0.2 }}
                      className="relative pl-6"
                    >
                      <span className={cn("absolute -left-[5px] top-1.5 h-2 w-2 rounded-full ring-4 ring-[var(--surface)]", visit.dot)} />
                      <p className="text-12 font-medium text-[var(--text-3)]">{visit.date}</p>
                      <p className="mt-0.5 text-14 font-semibold text-[var(--text)]">{visit.title}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="rounded bg-[var(--surface-2)] px-2 py-0.5 text-11 font-medium text-[var(--text-2)]">{visit.doctor}</span>
                        <span className="rounded bg-[var(--surface-2)] px-2 py-0.5 text-11 font-medium text-[var(--text-2)]">{visit.status}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function AboutProject() {
  const { t } = useTranslation('dashboard');
  return (
    <Section id="about" muted className="overflow-hidden">
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-[var(--surface)] shadow-xl ring-1 ring-[var(--border)]">
        {/* Decorative background gradients */}
        <div className="absolute -left-40 -top-40 h-96 w-96 rounded-full bg-[var(--primary)] opacity-[0.08] blur-[100px]" />
        <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-[var(--info)] opacity-[0.08] blur-[100px]" />

        <div className="relative z-10 grid gap-12 p-8 sm:p-16 lg:grid-cols-2 lg:items-center">
          <div>
            <Reveal>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--primary-weak)] bg-[var(--primary-bg)] px-3 py-1">
                <HeartHandshake size={14} className="text-[var(--primary)]" />
                <span className="text-12 font-semibold uppercase tracking-widest text-[var(--primary)]">
                  {t('landing.static.about.mission')}
                </span>
              </div>
              <h2 className="text-2xl font-bold leading-tight tracking-tight text-[var(--text)] sm:text-3xl lg:text-4xl lg:pr-8">
                {t('landing.static.about.titleA')}<span className="text-[var(--primary)]">{t('landing.static.about.titleB')}</span>{t('landing.static.about.titleC')}
              </h2>
            </Reveal>
          </div>
          <div className="relative">
            {/* Elegant vertical rule line for the text */}
            <div className="absolute bottom-0 left-0 top-0 hidden w-[2px] bg-gradient-to-b from-[var(--primary)] to-transparent opacity-20 lg:block" />

            <Reveal delay={0.1}>
              <div className="space-y-6 text-16 font-light leading-relaxed text-[var(--text-2)] lg:pl-10">
                <p>
                  {t('landing.static.about.p1')}
                </p>
                <p>
                  {t('landing.static.about.p2a')}<strong className="font-semibold text-[var(--text)]">{t('landing.static.about.p2b')}</strong>{t('landing.static.about.p2c')}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </Section>
  );
}

function BeforeAfter() {
  const { t } = useTranslation('dashboard');
  return (
    <Section id="before-after" className="overflow-hidden">
      <div className="text-center">
        <Reveal>
          <h2 className="text-2xl font-bold tracking-tight text-[var(--text)] sm:text-3xl lg:text-4xl">{t('landing.static.beforeAfter.title')}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-16 font-light text-[var(--text-2)]">{t('landing.static.beforeAfter.subtitle')}</p>
        </Reveal>
      </div>

      <div className="mx-auto mt-16 grid max-w-5xl gap-8 md:grid-cols-[1fr_auto_1fr] md:items-center">
        <Reveal delay={0.1}>
          <div className="relative overflow-hidden rounded-[32px] border border-[var(--danger-bg)] bg-[var(--surface)] p-5 sm:p-8">
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[var(--danger)] opacity-10 blur-[80px]" />

            <div className="relative z-10 mb-8 flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--danger-bg)] text-[var(--danger)] shadow-sm">
                <X size={28} strokeWidth={3} />
              </span>
              <h3 className="text-2xl font-bold text-[var(--text)]">{t('landing.static.beforeAfter.withoutTitle')}</h3>
            </div>

            <ul className="relative z-10 space-y-6 text-16 text-[var(--text-2)]">
              <li className="flex gap-4">
                <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--danger-bg)] text-[var(--danger)]">
                  <X size={14} strokeWidth={3} />
                </div>
                <p>{t('landing.static.beforeAfter.without1')}</p>
              </li>
              <li className="flex gap-4">
                <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--danger-bg)] text-[var(--danger)]">
                  <X size={14} strokeWidth={3} />
                </div>
                <p>{t('landing.static.beforeAfter.without2')}</p>
              </li>
              <li className="flex gap-4">
                <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--danger-bg)] text-[var(--danger)]">
                  <X size={14} strokeWidth={3} />
                </div>
                <p>{t('landing.static.beforeAfter.without3')}</p>
              </li>
            </ul>
          </div>
        </Reveal>

        {/* Separator / Arrow */}
        <div className="relative z-10 hidden flex-col items-center justify-center md:flex">
          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] shadow-sm">
            <ArrowRight size={24} strokeWidth={2} />
          </div>
        </div>

        <Reveal delay={0.2}>
          <div className="relative overflow-hidden rounded-[32px] border border-[var(--primary-weak)] bg-[var(--surface)] p-5 sm:p-8">
            <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-[var(--primary)] opacity-10 blur-[80px]" />
            <div className="absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-[var(--info)] opacity-10 blur-[80px]" />

            <div className="relative z-10 mb-8 flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--primary)] text-[var(--primary-fg)] shadow-md">
                <Check size={28} strokeWidth={3} />
              </span>
              <h3 className="text-2xl font-bold text-[var(--text)]">{t('landing.static.beforeAfter.withTitle')}</h3>
            </div>

            <ul className="relative z-10 space-y-6 text-16 text-[var(--text-2)]">
              <li className="flex gap-4">
                <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary-weak)] text-[var(--primary)]">
                  <Check size={14} strokeWidth={3} />
                </div>
                <p><strong className="font-semibold text-[var(--text)]">{t('landing.static.beforeAfter.with1A')}</strong>{t('landing.static.beforeAfter.with1B')}</p>
              </li>
              <li className="flex gap-4">
                <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary-weak)] text-[var(--primary)]">
                  <Check size={14} strokeWidth={3} />
                </div>
                <p><strong className="font-semibold text-[var(--text)]">{t('landing.static.beforeAfter.with2A')}</strong>{t('landing.static.beforeAfter.with2B')}</p>
              </li>
              <li className="flex gap-4">
                <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary-weak)] text-[var(--primary)]">
                  <Check size={14} strokeWidth={3} />
                </div>
                <p><strong className="font-semibold text-[var(--text)]">{t('landing.static.beforeAfter.with3A')}</strong>{t('landing.static.beforeAfter.with3B')}</p>
              </li>
            </ul>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

function HowItWorks() {
  const { t } = useTranslation('dashboard');
  return (
    <Section id="how-it-works" muted>
      <div className="mb-16 text-center">
        <Reveal>
          <span className="text-12 font-semibold uppercase tracking-widest text-[var(--primary)]">
            {t('landing.static.howItWorks.eyebrow')}
          </span>
          <h2 className="mt-3 text-2xl font-semibold text-[var(--text)] sm:text-3xl lg:text-4xl">{t('landing.static.howItWorks.title')}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-16 font-light text-[var(--text-2)]">{t('landing.static.howItWorks.subtitle')}</p>
        </Reveal>
      </div>

      <div className="relative mx-auto max-w-5xl">
        {/* Animated Connecting Line (Desktop) */}
        <div className="absolute left-[12%] right-[12%] top-10 hidden h-2 md:block">
          <svg className="h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 10">
            <motion.line
              x1="0" y1="5" x2="100" y2="5"
              stroke="var(--border-strong)"
              strokeWidth="2"
              strokeDasharray="4 4"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 2, ease: "easeInOut" }}
            />
          </svg>
        </div>

        <div className="grid gap-8 md:grid-cols-4">
          {[
            { icon: Smartphone, title: t('landing.static.howItWorks.step1Title'), desc: t('landing.static.howItWorks.step1Desc'), color: 'text-[var(--info)] bg-[var(--info-bg)]' },
            { icon: Clock, title: t('landing.static.howItWorks.step2Title'), desc: t('landing.static.howItWorks.step2Desc'), color: 'text-[var(--warning)] bg-[var(--warning-bg)]' },
            { icon: Stethoscope, title: t('landing.static.howItWorks.step3Title'), desc: t('landing.static.howItWorks.step3Desc'), color: 'text-[var(--primary)] bg-[var(--primary-weak)]' },
            { icon: Receipt, title: t('landing.static.howItWorks.step4Title'), desc: t('landing.static.howItWorks.step4Desc'), color: 'text-[var(--success)] bg-[var(--success-bg)]' }
          ].map((step, i) => (
            <div key={i} className="relative flex flex-col items-center text-center">
              <motion.div
                initial={{ opacity: 0.4, scale: 0.9, filter: "grayscale(100%)" }}
                whileInView={{ opacity: 1, scale: 1, filter: "grayscale(0%)" }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ delay: i * 0.66, duration: 0.5, type: "spring", stiffness: 300, damping: 20 }}
                className="flex flex-col items-center"
              >
                <motion.div
                  className={cn("relative z-10 mb-6 flex h-20 w-20 items-center justify-center rounded-[24px] shadow-sm ring-8 ring-[var(--surface-2)] cursor-default", step.color)}
                  whileHover={{ scale: 1.05, y: -4 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <step.icon size={32} strokeWidth={2} />
                </motion.div>
                <h3 className="text-18 font-semibold text-[var(--text)]">{step.title}</h3>
                <p className="mt-2 px-2 text-14 font-light text-[var(--text-2)]">{step.desc}</p>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function Features() {
  const { t } = useTranslation('dashboard');

  return (
    <Section id="features" muted>
      <div className="mb-16 text-center">
        <Reveal>
          <span className="text-12 font-semibold uppercase tracking-widest text-[var(--primary)]">
            {t('landing.reference.overview.eyebrow')}
          </span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-[var(--text)] sm:text-3xl lg:text-4xl">{t('landing.reference.overview.title')}</h2>
          <p className="mx-auto mt-5 max-w-2xl text-16 font-light text-[var(--text-2)]">{t('landing.reference.overview.body')}</p>
        </Reveal>
      </div>

      <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:grid-rows-[auto_auto]">
        {/* Card 1: Unified Patient Records (Col Span 2, Row Span 2) */}
        <RevealScale className="sm:col-span-2 lg:col-span-2 lg:row-span-2">
          <div className="relative flex h-full min-h-[400px] flex-col overflow-hidden rounded-[32px] border border-[var(--primary-weak)] bg-[var(--surface)] p-8 shadow-xl ring-1 ring-[var(--border)] sm:p-12">
            <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-[var(--primary)] opacity-10 blur-[80px]" />
            <div className="relative z-10 mb-8">
              <span className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--primary-bg)] text-[var(--primary)] shadow-sm">
                <HeartHandshake size={28} strokeWidth={2} />
              </span>
              <h3 className="mb-3 text-24 font-bold text-[var(--text)] sm:text-3xl">{t('landing.static.featuresVisual.unifiedTitle')}</h3>
              <p className="max-w-md text-16 font-light leading-relaxed text-[var(--text-2)] sm:text-18">
                {t('landing.static.featuresVisual.unifiedDesc')}
              </p>
            </div>
            {/* Visual element: A mini medical record mockup */}
            <div className="relative z-10 mt-auto overflow-hidden rounded-t-xl border border-b-0 border-[var(--border)] bg-[var(--surface-2)] shadow-inner">
              <div className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--surface)] p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--primary-bg)] text-14 font-bold text-[var(--primary)]">RS</div>
                  <div>
                    <p className="text-14 font-semibold text-[var(--text)]">Rahul Sharma</p>
                    <p className="text-12 text-[var(--text-2)]">{t('landing.static.heroVisual.lastVisit')}</p>
                  </div>
                </div>
                <div className="rounded bg-[var(--success-bg)] px-3 py-1 text-11 font-bold uppercase tracking-wider text-[var(--success)]">{t('landing.static.featuresVisual.rxReady')}</div>
              </div>
              <div className="flex flex-col gap-3 p-4 sm:flex-row sm:p-6">
                <div className="flex-1 rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4 shadow-sm">
                  <p className="mb-2 text-11 font-semibold uppercase tracking-wider text-[var(--text-3)]">{t('landing.static.featuresVisual.diagnosis')}</p>
                  <div className="mb-2 h-2 w-3/4 rounded-full bg-[var(--surface-3)]" />
                  <div className="h-2 w-1/2 rounded-full bg-[var(--surface-3)]" />
                </div>
                <div className="flex-1 rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4 shadow-sm">
                  <p className="mb-2 text-11 font-semibold uppercase tracking-wider text-[var(--text-3)]">{t('landing.static.featuresVisual.prescription')}</p>
                  <div className="mb-2 h-2 w-full rounded-full bg-[var(--surface-3)]" />
                  <div className="h-2 w-2/3 rounded-full bg-[var(--surface-3)]" />
                </div>
              </div>
            </div>
          </div>
        </RevealScale>

        {/* Card 2: Smart Scheduling (Col Span 1, Row Span 1) */}
        <RevealScale delay={0.1}>
          <div className="flex h-full min-h-[250px] flex-col justify-between rounded-[32px] border border-[var(--border)] bg-[var(--surface-2)] p-5 sm:p-6">
            <div>
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--success-bg)] text-[var(--success)] shadow-sm">
                <CalendarClock size={24} strokeWidth={2} />
              </span>
              <h3 className="mb-2 text-20 font-bold text-[var(--text)]">{t('landing.reference.overview.scheduling.title')}</h3>
              <p className="text-15 font-light leading-relaxed text-[var(--text-2)]">{t('landing.reference.overview.scheduling.body')}</p>
            </div>
          </div>
        </RevealScale>

        {/* Card 3: Live Token System (Col Span 1, Row Span 1) */}
        <RevealScale delay={0.2}>
          <div className="flex h-full min-h-[250px] flex-col justify-between rounded-[32px] border border-[var(--border)] bg-[var(--surface-2)] p-5 sm:p-6">
            <div>
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--info-bg)] text-[var(--info)] shadow-sm">
                <Smartphone size={24} strokeWidth={2} />
              </span>
              <h3 className="mb-2 text-20 font-bold text-[var(--text)]">{t('landing.reference.overview.queue.title')}</h3>
              <p className="text-15 font-light leading-relaxed text-[var(--text-2)]">{t('landing.reference.overview.queue.body')}</p>
            </div>
          </div>
        </RevealScale>

        {/* Card 4: Billing & Invoices (Col Span 2, Row Span 1) */}
        <RevealScale delay={0.3} className="sm:col-span-2">
          <div className="flex h-full min-h-[200px] flex-col justify-between rounded-[32px] border border-[var(--border)] bg-[var(--surface-2)] p-5 sm:p-6 sm:flex-row sm:items-center">
            <div className="max-w-md">
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--warning-bg)] text-[var(--warning)] shadow-sm">
                <Receipt size={24} strokeWidth={2} />
              </span>
              <h3 className="mb-2 text-20 font-bold text-[var(--text)]">{t('landing.reference.overview.billing.title')}</h3>
              <p className="text-15 font-light leading-relaxed text-[var(--text-2)]">{t('landing.reference.overview.billing.body')}</p>
            </div>
            {/* Visual element */}
            <div className="mt-8 flex items-center gap-4 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-sm sm:mt-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--success-bg)] text-[var(--success)]"><Check size={20} strokeWidth={3} /></div>
              <div>
                <p className="text-14 font-semibold text-[var(--text)]">{t('landing.static.featuresVisual.paid')}</p>
                <p className="text-12 text-[var(--text-2)]">{t('landing.static.featuresVisual.receiptSent')}</p>
              </div>
            </div>
          </div>
        </RevealScale>

        {/* Card 5: Staff Access Control (Col Span 1, Row Span 1) */}
        <RevealScale delay={0.4}>
          <div className="flex h-full min-h-[200px] flex-col justify-between rounded-[32px] border border-[var(--border)] bg-[var(--surface-2)] p-5 sm:p-6">
            <div>
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--primary-weak)] text-[var(--primary)] shadow-sm">
                <ShieldCheck size={24} strokeWidth={2} />
              </span>
              <h3 className="mb-2 text-20 font-bold text-[var(--text)]">{t('landing.reference.overview.rbac.title')}</h3>
              <p className="text-15 font-light leading-relaxed text-[var(--text-2)]">{t('landing.reference.overview.rbac.body')}</p>
            </div>
          </div>
        </RevealScale>

      </div>
    </Section>
  );
}

function Pricing() {
  const { t } = useTranslation('dashboard');
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');

  return (
    <Section id="pricing">
      <div className="mb-12 text-center">
        <Reveal>
          <span className="text-12 font-semibold uppercase tracking-widest text-[var(--primary)]">
            {t('landing.pricing.eyebrow')}
          </span>
          <h2 className="mx-auto mt-3 max-w-2xl text-2xl font-semibold text-[var(--text)] sm:text-3xl lg:text-4xl">
            {t('landing.pricing.title')}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-16 font-light text-[var(--text-2)]">
            {t('landing.pricing.body')}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mx-auto mt-8 flex w-fit items-center rounded-full border border-[var(--border)] bg-[var(--surface-2)] p-1">
            <button
              onClick={() => setBilling('monthly')}
              className={cn('rounded-full px-5 py-2 text-14 font-medium transition-all', billing === 'monthly' ? 'bg-[var(--surface)] text-[var(--text)] shadow-sm' : 'text-[var(--text-2)] hover:text-[var(--text)]')}
            >
              {t('landing.pricing.monthly')}
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className={cn('rounded-full px-5 py-2 text-14 font-medium transition-all flex items-center gap-2', billing === 'yearly' ? 'bg-[var(--surface)] text-[var(--text)] shadow-sm' : 'text-[var(--text-2)] hover:text-[var(--text)]')}
            >
              {t('landing.pricing.yearly')}
              <span className="rounded-full bg-[var(--success-bg)] px-2 py-0.5 text-11 font-bold text-[var(--success)]">{t('landing.pricing.yearlySave')}</span>
            </button>
          </div>
        </Reveal>
      </div>

      <div className="mx-auto mt-12 grid max-w-[1100px] gap-6 md:grid-cols-3">
        {/* Free Plan */}
        <RevealScale delay={0.1}>
          <GlassCard className="flex h-full flex-col p-6 lg:p-8" hover={false}>
            <h3 className="text-24 font-semibold text-[var(--text)]">{t('landing.pricing.free.name')}</h3>
            <p className="mt-1 min-h-[40px] text-14 text-[var(--text-2)]">{t('landing.pricing.free.tagline')}</p>
            <div className="my-6">
              <span className="text-4xl font-bold text-[var(--text)]">₹0</span>
            </div>
            <MotionButton variant="outline" className="w-full">{t('landing.static.pricingStatic.getStarted')}</MotionButton>

            <ul className="mt-6 space-y-3">
              {(['landing.pricing.free.feat1', 'landing.pricing.free.feat2', 'landing.pricing.free.feat3', 'landing.pricing.free.feat4'] as const).map(key => (
                <li key={key} className="flex items-center gap-3 text-14 font-light text-[var(--text-2)]">
                  <CheckCircle2 className="text-[var(--text-3)]" size={16} />
                  {t(key)}
                </li>
              ))}
            </ul>
          </GlassCard>
        </RevealScale>

        {/* Starter Plan */}
        <RevealScale delay={0.2}>
          <GlassCard className="flex h-full flex-col p-6 lg:p-8" hover={false}>
            <h3 className="text-24 font-semibold text-[var(--text)]">{t('landing.pricing.pro.name')}</h3>
            <p className="mt-1 min-h-[40px] text-14 text-[var(--text-2)]">{t('landing.pricing.pro.tagline')}</p>
            <div className="my-6">
              <span className="text-4xl font-bold text-[var(--text)]">{billing === 'monthly' ? '₹1,999' : '₹19,990'}</span>
              <span className="text-14 text-[var(--text-2)]">/{billing === 'monthly' ? 'mo' : 'yr'}</span>
            </div>
            <MotionButton href={`${CLINIC_URL}/register`} variant="outline" className="w-full">{t('landing.static.pricingStatic.startTrial')}</MotionButton>

            <ul className="mt-6 space-y-3">
              {(['landing.pricing.pro.feat1', 'landing.pricing.pro.feat2', 'landing.pricing.pro.feat3', 'landing.pricing.pro.feat4', 'landing.pricing.pro.feat5'] as const).map(key => (
                <li key={key} className="flex items-center gap-3 text-14 font-light text-[var(--text-2)]">
                  <CheckCircle2 className="text-[var(--success)]" size={16} />
                  {t(key)}
                </li>
              ))}
            </ul>
          </GlassCard>
        </RevealScale>

        {/* Polyclinic Plan */}
        <RevealScale delay={0.3}>
          <div className="relative h-full pt-4 md:pt-0">
            <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-[var(--primary)] px-4 py-1 text-11 font-semibold uppercase tracking-widest text-[var(--primary-fg)] shadow-sm md:-top-4">
              {t('landing.static.pricingStatic.mostPopular')}
            </div>
            <GlassCard className="relative flex h-full flex-col border-[var(--primary)] p-6 ring-1 ring-[var(--primary-weak)] lg:p-8" hover={false}>

              <h3 className="text-24 font-semibold text-[var(--text)]">{t('landing.pricing.clinicPlus.name')}</h3>
              <p className="mt-1 min-h-[40px] text-14 text-[var(--text-2)]">{t('landing.pricing.clinicPlus.tagline')}</p>
              <div className="my-6">
                <span className="text-4xl font-bold text-[var(--text)]">{billing === 'monthly' ? '₹4,999' : '₹49,990'}</span>
                <span className="text-14 text-[var(--text-2)]">/{billing === 'monthly' ? 'mo' : 'yr'}</span>
              </div>
              <MotionButton href={`${CLINIC_URL}/register`} variant="primary" className="w-full">{t('landing.static.pricingStatic.startTrial')}</MotionButton>

              <ul className="mt-6 space-y-3">
                {(['landing.pricing.clinicPlus.feat1', 'landing.pricing.clinicPlus.feat2', 'landing.pricing.clinicPlus.feat3', 'landing.pricing.clinicPlus.feat4', 'landing.pricing.clinicPlus.feat5'] as const).map(key => (
                  <li key={key} className="flex items-center gap-3 text-14 font-light text-[var(--text-2)]">
                    <CheckCircle2 className="text-[var(--primary)]" size={16} />
                    <span className="text-[var(--text)]">{t(key)}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          </div>
        </RevealScale>
      </div>
    </Section>
  );
}

function Footer() {
  const { t } = useTranslation('dashboard');
  return (
    <footer id="contact" className="border-t border-[var(--border)] bg-[var(--surface-2)] py-12 lg:py-16">
      <Container>
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <img src="/long_logo.svg" alt="DocCare+" className="h-8 w-auto mb-6" />
            <p className="max-w-sm text-15 font-light leading-relaxed text-[var(--text-2)]">
              {t('landing.static.footer.tagline')}
            </p>
          </div>
          <div>
            <h4 className="mb-4 text-14 font-semibold text-[var(--text)] uppercase tracking-wider">{t('landing.static.footer.contact')}</h4>
            <ul className="space-y-3 text-14 font-light text-[var(--text-2)]">
              <li>hello@doccareplus.com</li>
              <li>+91 98765 43210</li>
              <li>Bangalore, India</li>
            </ul>
          </div>
          <div>
            <h4 className="mb-4 text-14 font-semibold text-[var(--text)] uppercase tracking-wider">{t('landing.static.footer.legal')}</h4>
            <ul className="space-y-3 text-14 font-light text-[var(--text-2)]">
              <li><a href="/privacy" className="hover:text-[var(--text)] hover:underline">{t('landing.static.footer.privacy')}</a></li>
              <li><a href="/terms" className="hover:text-[var(--text)] hover:underline">{t('landing.static.footer.terms')}</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 border-t border-[var(--border)] pt-8 text-center text-13 font-light text-[var(--text-3)]">
          &copy; {new Date().getFullYear()} DocCare+. {t('landing.static.footer.rights')}
        </div>
      </Container>
    </footer>
  );
}

function DashboardPreview() {
  const { t } = useTranslation('dashboard');
  return (
    <Section id="dashboard-preview" className="overflow-hidden bg-[var(--surface-2)]/50 border-y border-[var(--border)]">
      <div className="mb-12 text-center">
        <Reveal>
          <h2 className="text-2xl font-bold tracking-tight text-[var(--text)] sm:text-3xl lg:text-4xl">{t('landing.static.dashboardPreview.title')}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-16 font-light text-[var(--text-2)]">{t('landing.static.dashboardPreview.subtitle')}</p>
        </Reveal>
      </div>

      <div className="relative mx-auto max-w-6xl">
        <div className="grid gap-4 md:grid-cols-3 lg:gap-6">

          {/* Main Dashboard */}
          <RevealScale delay={0.1}>
            <div className="group relative overflow-hidden rounded-[20px] border border-[var(--border)] bg-[var(--surface)] p-1.5 shadow-xl ring-1 ring-[var(--border-strong)] transition-transform hover:-translate-y-1 h-full">
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface)] via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100 z-10" />
              <img src="/dashboard-mockup.png" alt="Doctor Dashboard" className="w-full h-[280px] rounded-[14px] object-cover object-top" />
              <div className="absolute bottom-5 left-5 z-20 translate-y-4 opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
                <h3 className="text-18 font-bold text-white drop-shadow-md">Doctor Workspace</h3>
                <p className="text-13 font-medium text-white/90 drop-shadow-md">Unified records & schedule</p>
              </div>
            </div>
          </RevealScale>

          {/* Patient App */}
          <RevealScale delay={0.2}>
            <div className="group relative overflow-hidden rounded-[20px] border border-[var(--border)] bg-[var(--surface)] p-1.5 shadow-xl ring-1 ring-[var(--border-strong)] transition-transform hover:-translate-y-1 h-full">
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface)] via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100 z-10" />
              <img src="/patient-mobile-mockup.png" alt="Patient Mobile App" className="w-full h-[280px] rounded-[14px] object-cover object-top" />
              <div className="absolute bottom-5 left-5 z-20 translate-y-4 opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
                <h3 className="text-18 font-bold text-white drop-shadow-md">Patient Experience</h3>
                <p className="text-13 font-medium text-white/90 drop-shadow-md">Live tokens & Rx</p>
              </div>
            </div>
          </RevealScale>

          {/* Receptionist View */}
          <RevealScale delay={0.3}>
            <div className="group relative overflow-hidden rounded-[20px] border border-[var(--border)] bg-[var(--surface)] p-1.5 shadow-xl ring-1 ring-[var(--border-strong)] transition-transform hover:-translate-y-1 h-full">
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface)] via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100 z-10" />
              <img src="/receptionist-billing-mockup.png" alt="Receptionist Billing" className="w-full h-[280px] rounded-[14px] object-cover object-top" />
              <div className="absolute bottom-5 left-5 z-20 translate-y-4 opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
                <h3 className="text-18 font-bold text-white drop-shadow-md">Front Desk</h3>
                <p className="text-13 font-medium text-white/90 drop-shadow-md">Fast billing & receipts</p>
              </div>
            </div>
          </RevealScale>

        </div>

        {/* Ambient Glow */}
        <div className="absolute -inset-10 -z-10 rounded-[4rem] bg-gradient-to-tr from-[var(--primary)] via-[var(--info)] to-[var(--warning)] opacity-15 blur-[100px]" />
      </div>
    </Section>
  );
}

/**
 * Testimonial copy keys, listed literally so the i18n key type is checked statically. Building them as
 * `quote${n}` template strings would defeat that, since i18next types keys as literals.
 */
const TESTIMONIAL_KEYS = [
  {
    quote: 'landing.static.testimonials.quote1',
    author: 'landing.static.testimonials.author1',
    role: 'landing.static.testimonials.role1',
  },
  {
    quote: 'landing.static.testimonials.quote2',
    author: 'landing.static.testimonials.author2',
    role: 'landing.static.testimonials.role2',
  },
  {
    quote: 'landing.static.testimonials.quote3',
    author: 'landing.static.testimonials.author3',
    role: 'landing.static.testimonials.role3',
  },
] as const;

function Testimonials() {
  const { t } = useTranslation('dashboard');
  return (
    <Section id="testimonials" className="bg-[var(--surface)]">
      <div className="mb-16 text-center">
        <Reveal>
          <h2 className="text-2xl font-bold tracking-tight text-[var(--text)] sm:text-3xl lg:text-4xl">{t('landing.static.testimonials.title')}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-16 font-light text-[var(--text-2)]">{t('landing.static.testimonials.subtitle')}</p>
        </Reveal>
      </div>
      <div className="grid gap-6 md:grid-cols-3 max-w-6xl mx-auto">
        {TESTIMONIAL_KEYS.map((testimonial, index) => (
          <RevealScale key={testimonial.quote} delay={0.1 * (index + 1)}>
            <GlassCard className="flex h-full flex-col p-5 sm:p-6" hover={false}>
              <div className="flex text-[var(--warning)] mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}
              </div>
              <p className="mb-6 flex-1 text-16 font-light leading-relaxed text-[var(--text)]">"{t(testimonial.quote)}"</p>
              <div>
                <p className="font-semibold text-[var(--text)]">{t(testimonial.author)}</p>
                <p className="text-14 text-[var(--text-2)]">{t(testimonial.role)}</p>
              </div>
            </GlassCard>
          </RevealScale>
        ))}
      </div>
    </Section>
  );
}

export function LandingExperienceBody() {
  const { i18n } = useTranslation('dashboard');
  return (
    <div className="flex flex-col" lang={i18n.language}>
      <Navbar />
      <Hero />
      <DashboardPreview />
      <AboutProject />
      <HowItWorks />
      <BeforeAfter />
      <Features />
      <Testimonials />
      <Pricing />
      <Footer />
    </div>
  );
}
